import Cookies from "js-cookie";
import { getAnalyticsUserId } from "./analyticsIdentity";
import { getAuthHeaders, getJwt } from "./authHeaders";
import {
    trackFriendInviteSent,
    trackFriendJoinedFromInvite,
    trackGameInteraction,
    trackInviteFlowOpened,
    trackPlayWithFriendClicked,
} from "./frontendTrackingEvents";
import { isShareCancelled, nativeShareData } from "./nativeShare";
import {
    buildBullionMatchLandingInviteMessage,
    buildBullionPrivateContestInviteMessage,
    type InviteCopyContext,
} from "./shareInviteCopy";

const STORAGE_KEY = "c8_bullion_pending_private_invite";
const MATCH_INVITE_STORAGE_KEY = "c8_bullion_pending_match_invite";

export function clearBullionPendingInviteFromStorage(): void {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(MATCH_INVITE_STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

export type BullionLinkPendingInvite = {
    matchId: string;
    gameId: string;
    inviteCode?: string;
    inviterPlayerId?: string;
};

export type BullionMatchLandingPendingInvite = {
    matchId: string;
    inviterPlayerId: string;
};

/** Decode `back=/bullion?match=&game=&invite=` from login/signup query and persist. */
export function persistBullionPrivateInviteFromBackParam(back: string | null | undefined): void {
    if (!back || typeof back !== "string") return;
    const trimmed = back.trim();
    if (!trimmed) return;
    const withSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    if (!withSlash.startsWith("/bullion")) return;
    const qIndex = withSlash.indexOf("?");
    if (qIndex === -1) return;
    persistBullionPrivateInviteFromRouterSearch(withSlash.slice(qIndex));
}

export function persistBullionPrivateInviteFromSearch(search: string): void {
    try {
        const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
        const match = params.get("match") || params.get("matchId");
        const game = params.get("game") || params.get("gameId");
        if (!match?.trim() || !game?.trim()) return;
        const matchTrim = match.trim();
        const gameTrim = game.trim();
        const inviteFromUrl = params.get("invite")?.trim() || "";
        const invitedByFromUrl = params.get("invitedBy") || params.get("invited_by");
        const existing = readBullionPendingWithGame();
        const sameContest =
            existing &&
            String(existing.matchId).trim() === matchTrim &&
            String(existing.gameId).trim() === gameTrim;
        const inviteCode =
            inviteFromUrl ||
            (sameContest && existing?.inviteCode ? String(existing.inviteCode).trim() : "");
        const inviterPlayerId =
            invitedByFromUrl?.trim() ||
            (sameContest && existing?.inviterPlayerId ? String(existing.inviterPlayerId).trim() : "");
        const payload: BullionLinkPendingInvite = {
            matchId: matchTrim,
            gameId: gameTrim,
            ...(inviteCode ? { inviteCode } : {}),
            ...(inviterPlayerId ? { inviterPlayerId } : {}),
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

/** Match landing share: `/bullion?match=…&invitedBy=…` (no `game`) — parity with Squares invite persistence. */
export function persistBullionMatchLandingInviteFromSearch(search: string): void {
    try {
        const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
        const match = params.get("match") || params.get("matchId");
        const game = params.get("game") || params.get("gameId");
        const invitedBy = params.get("invitedBy") || params.get("invited_by");
        if (!match?.trim() || !invitedBy?.trim()) return;
        if (game?.trim()) return;
        const payload: BullionMatchLandingPendingInvite = {
            matchId: match.trim(),
            inviterPlayerId: invitedBy.trim(),
        };
        sessionStorage.setItem(MATCH_INVITE_STORAGE_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

export function persistBullionPrivateInviteFromRouterSearch(search: string): void {
    persistBullionPrivateInviteFromSearch(search);
    persistBullionMatchLandingInviteFromSearch(search);
    try {
        const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
        persistBullionPrivateInviteFromBackParam(params.get("back"));
    } catch {
        /* ignore */
    }
}

export type BuildBullionPrivateContestShareUrlArgs = {
    matchId: string;
    gameId: number;
    inviteCode: string;
    inviterPlayerId?: string | null;
};

export function buildBullionPrivateContestShareUrl(args: BuildBullionPrivateContestShareUrlArgs): string {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    const params = new URLSearchParams();
    params.set("match", args.matchId.trim());
    params.set("game", String(args.gameId));
    const invite = args.inviteCode.trim();
    if (invite) params.set("invite", invite);
    const pid = args.inviterPlayerId != null ? String(args.inviterPlayerId).trim() : "";
    if (pid) params.set("invitedBy", pid);
    return `${base}/bullion?${params.toString()}`;
}

export type BuildBullionMatchLandingShareUrlArgs = {
    matchId: string;
    inviterPlayerId?: string | null;
    /** Pin a specific public (or private) contest — same `game` param as private contest links. */
    gameId?: number | string | null;
};

/** Public match lobby (`/bullion?match=…`) — optional `game` + `invitedBy` for a concrete contest invite. */
export function buildBullionMatchLandingShareUrl(args: BuildBullionMatchLandingShareUrlArgs): string {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    const params = new URLSearchParams();
    params.set("match", args.matchId.trim());
    const gidRaw = args.gameId;
    const gidNum = gidRaw != null && gidRaw !== "" ? Number(gidRaw) : NaN;
    if (Number.isFinite(gidNum) && gidNum > 0) params.set("game", String(gidNum));
    const pid = args.inviterPlayerId != null ? String(args.inviterPlayerId).trim() : "";
    if (pid) params.set("invitedBy", pid);
    return `${base}/bullion?${params.toString()}`;
}

type ShareArgs = BuildBullionPrivateContestShareUrlArgs &
    InviteCopyContext & {
        userId?: string | number | null;
        cta_location?: string;
    };

/**
 * Share / copy invite link for a private BULLION contest (Squares-style native share → clipboard fallback).
 */
export function shareBullionPrivateContestInvite(
    args: ShareArgs & {
        shareFeedbackTimeoutRef: { current: number | null };
        setShareFeedback: (v: "copied" | "shared" | null) => void;
    }
): void {
    const {
        matchId,
        gameId,
        inviteCode,
        inviterPlayerId,
        userId,
        cta_location,
        shareFeedbackTimeoutRef,
        setShareFeedback,
    } = args;
    const pathname = typeof window !== "undefined" ? window.location.pathname : "/bullion";
    trackPlayWithFriendClicked({
        user_id: userId ?? undefined,
        cta_name: "bullion_private_contest_invite",
        cta_location: cta_location ?? "bullion",
        fixture_id: matchId,
        game_type: "bullion",
        visibility: "private",
        pathname,
    });
    trackInviteFlowOpened({
        user_id: userId ?? undefined,
        entry_point: "bullion_private_contest_share",
        fixture_id: matchId,
        game_type: "bullion",
        visibility: "private",
        pathname,
    });
    const url = buildBullionPrivateContestShareUrl({ matchId, gameId, inviteCode, inviterPlayerId });
    const message = buildBullionPrivateContestInviteMessage({
        teams: args.teams,
        matchTitle: args.matchTitle,
        matchName: args.matchName,
        tournament: args.tournament,
        contestName: args.contestName,
        contestKind: args.contestKind ?? "battle",
    });
    const textWithLink = `${message}\n${url}`;
    const trackPrivateBattleShared = (method: "clipboard" | "native_share") => {
        trackGameInteraction({
            user_id: userId ?? undefined,
            interaction_type: "private_battle_link_shared",
            interaction_value: method,
            game_type: "bullion",
            fixture_id: matchId,
            game_id: gameId,
            pathname,
        });
    };

    if (shareFeedbackTimeoutRef.current != null) {
        window.clearTimeout(shareFeedbackTimeoutRef.current);
    }
    const showCopied = () => {
        setShareFeedback("copied");
        shareFeedbackTimeoutRef.current = window.setTimeout(() => setShareFeedback(null), 2500);
    };
    const copyUrl = () => {
        if (typeof navigator === "undefined" || !navigator.clipboard) return;
        navigator.clipboard.writeText(textWithLink).then(
            () => {
                trackFriendInviteSent({
                    user_id: userId ?? undefined,
                    invite_method: "clipboard",
                    fixture_id: matchId,
                    game_type: "bullion",
                    visibility: "private",
                    pathname,
                });
                trackPrivateBattleShared("clipboard");
                showCopied();
            },
            () => setShareFeedback(null)
        );
    };

    if (typeof navigator !== "undefined" && navigator.share) {
        navigator
            .share(nativeShareData(message, url))
            .then(() => {
                trackFriendInviteSent({
                    user_id: userId ?? undefined,
                    invite_method: "native_share",
                    fixture_id: matchId,
                    game_type: "bullion",
                    visibility: "private",
                    pathname,
                });
                trackPrivateBattleShared("native_share");
                setShareFeedback("shared");
                shareFeedbackTimeoutRef.current = window.setTimeout(() => setShareFeedback(null), 2500);
            })
            .catch((error) => {
                if (!isShareCancelled(error)) copyUrl();
            });
    } else {
        copyUrl();
    }
}

export type ShareBullionMatchLandingArgs = {
    matchId: string;
    inviterPlayerId?: string | null;
    gameId?: number | string | null;
    userId?: string | number | null;
    cta_location?: string;
    shareFeedbackTimeoutRef: { current: number | null };
    setShareFeedback: (v: "copied" | "shared" | null) => void;
} & InviteCopyContext;

/**
 * Share / copy link to this fixture’s BULLION match landing (`/bullion?match=…`).
 */
export function shareBullionMatchLandingInvite(args: ShareBullionMatchLandingArgs): void {
    const {
        matchId,
        inviterPlayerId,
        gameId,
        userId,
        cta_location,
        shareFeedbackTimeoutRef,
        setShareFeedback,
    } = args;
    const mid = matchId.trim();
    if (!mid) return;

    const pathname = typeof window !== "undefined" ? window.location.pathname : "/bullion";
    const inviteContext = {
        fixture_id: mid,
        game_type: "bullion" as const,
        visibility: "public" as const,
    };

    trackPlayWithFriendClicked({
        user_id: userId ?? undefined,
        cta_name: "bullion_match_landing_invite",
        cta_location: cta_location ?? "bullion_match_landing",
        pathname,
        ...inviteContext,
    });
    trackInviteFlowOpened({
        user_id: userId ?? undefined,
        entry_point: "bullion_match_landing_share",
        pathname,
        ...inviteContext,
    });
    const url = buildBullionMatchLandingShareUrl({ matchId: mid, inviterPlayerId, gameId });
    const message = buildBullionMatchLandingInviteMessage({
        teams: args.teams,
        matchTitle: args.matchTitle,
        matchName: args.matchName,
        tournament: args.tournament,
        contestName: args.contestName,
        contestKind: args.contestKind ?? (args.gameId ? "arena" : null),
    });
    const textWithLink = `${message}\n${url}`;

    if (shareFeedbackTimeoutRef.current != null) {
        window.clearTimeout(shareFeedbackTimeoutRef.current);
    }
    const showCopied = () => {
        setShareFeedback("copied");
        shareFeedbackTimeoutRef.current = window.setTimeout(() => setShareFeedback(null), 2500);
    };
    const copyUrl = () => {
        if (typeof navigator === "undefined" || !navigator.clipboard) return;
        navigator.clipboard.writeText(textWithLink).then(
            () => {
                trackFriendInviteSent({
                    user_id: userId ?? undefined,
                    invite_method: "clipboard",
                    pathname,
                    ...inviteContext,
                });
                showCopied();
            },
            () => setShareFeedback(null)
        );
    };

    if (typeof navigator !== "undefined" && navigator.share) {
        navigator
            .share(nativeShareData(message, url))
            .then(() => {
                trackFriendInviteSent({
                    user_id: userId ?? undefined,
                    invite_method: "native_share",
                    pathname,
                    ...inviteContext,
                });
                setShareFeedback("shared");
                shareFeedbackTimeoutRef.current = window.setTimeout(() => setShareFeedback(null), 2500);
            })
            .catch((error) => {
                if (!isShareCancelled(error)) copyUrl();
            });
    } else {
        copyUrl();
    }
}

export function readBullionPendingPrivateInviteFromStorage(): BullionLinkPendingInvite | null {
    return readBullionPendingWithGame();
}

/**
 * Invite token for `/bullion/open-game` after URL cleanup strips `invite=` (persisted on first landing).
 */
export function readBullionPendingPrivateInviteCode(matchId?: string, gameId?: number): string {
    const pending = readBullionPendingWithGame();
    if (!pending?.inviteCode) return "";
    const code = String(pending.inviteCode).trim();
    if (!code) return "";
    const mid = matchId != null ? String(matchId).trim() : "";
    if (mid && pending.matchId !== mid) return "";
    if (gameId != null && Number.isFinite(gameId) && gameId > 0) {
        const pendingGid = parseInt(String(pending.gameId), 10);
        if (Number.isFinite(pendingGid) && pendingGid !== gameId) return "";
    }
    return code;
}

function readBullionPendingWithGame(): BullionLinkPendingInvite | null {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const o = JSON.parse(raw) as Partial<BullionLinkPendingInvite>;
        if (!o?.matchId?.trim() || !o?.gameId?.trim()) return null;
        return {
            matchId: String(o.matchId).trim(),
            gameId: String(o.gameId).trim(),
            ...(o.inviteCode != null && String(o.inviteCode).trim() !== ""
                ? { inviteCode: String(o.inviteCode).trim() }
                : {}),
            ...(o.inviterPlayerId != null && String(o.inviterPlayerId).trim() !== ""
                ? { inviterPlayerId: String(o.inviterPlayerId).trim() }
                : {}),
        };
    } catch {
        return null;
    }
}

function readBullionPendingMatchOnly(): BullionMatchLandingPendingInvite | null {
    try {
        const raw = sessionStorage.getItem(MATCH_INVITE_STORAGE_KEY);
        if (!raw) return null;
        const o = JSON.parse(raw) as Partial<BullionMatchLandingPendingInvite>;
        if (!o?.matchId?.trim() || !o?.inviterPlayerId?.trim()) return null;
        return {
            matchId: String(o.matchId).trim(),
            inviterPlayerId: String(o.inviterPlayerId).trim(),
        };
    } catch {
        return null;
    }
}

/**
 * After login (or while already logged in), create `bullion_match_challenge` from a stored BULLION share URL.
 * Clears session keys on success; on definitive 400s clears so the user is not stuck retrying.
 */
export async function tryClaimBullionLinkInviteFromStorage(
    analyticsUserIdHint?: string | number | null
): Promise<void> {
    const withGame = readBullionPendingWithGame();
    const matchOnly = withGame ? null : readBullionPendingMatchOnly();
    if (!withGame && !matchOnly) return;
    if (!Cookies.get("c8_session_token") && !getJwt()) return;

    const inviterPid = (withGame?.inviterPlayerId || matchOnly?.inviterPlayerId || "").trim();
    if (!inviterPid) return;

    const body: Record<string, string | number> = {
        match_id: withGame ? withGame.matchId : matchOnly!.matchId,
        inviter_player_id: inviterPid,
    };
    if (withGame) {
        const gid = parseInt(withGame.gameId, 10);
        if (Number.isFinite(gid) && gid > 0) body.game_id = gid;
        const inv = withGame.inviteCode?.trim();
        if (inv) body.invite_code = inv;
    }

    const clearPending = () => {
        clearBullionPendingInviteFromStorage();
    };

    try {
        const res = await fetch(`${process.env.API_URL}/account/bullion-invite-from-link/claim`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(body),
        });
        if (res.ok) {
            clearPending();
            const uid = analyticsUserIdHint ?? getAnalyticsUserId();
            trackFriendJoinedFromInvite({
                user_id: uid != null && String(uid).trim() !== "" ? uid : undefined,
                invite_method: "link",
                fixture_id: withGame ? withGame.matchId : matchOnly!.matchId,
                game_type: "bullion",
                visibility: withGame ? "private" : "public",
                entry_point: withGame ? "bullion_private_contest_link" : "bullion_match_landing_link",
                pathname: typeof window !== "undefined" ? window.location.pathname : "/bullion",
            });
            return;
        }
        if (res.status === 400) {
            const j = (await res.json().catch(() => ({}))) as { error?: string };
            const err = j?.error;
            if (
                err === "inviter_not_found" ||
                err === "self" ||
                err === "game_match_mismatch" ||
                err === "game_not_found" ||
                err === "invite_required" ||
                err === "contest_not_open" ||
                err === "no_open_public_game"
            ) {
                clearPending();
            }
        }
    } catch {
        /* keep pending for retry */
    }
}
